import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import {
  coachingApi,
  getPaymentAuthenticationRequired,
  InvalidApiResponseError,
  isApiError,
} from "../api";
import type { CoachingApi, CoachingAvailabilityCheckInput, CoachingAvailabilityPreviewQuery } from "../api/coachingContracts";
import { useAuth } from "./useAuth";

function useCoachingRead<T>(
  scope: readonly unknown[],
  read: (signal: AbortSignal) => Promise<T>,
  enabled = true,
) {
  const { user, loading } = useAuth();
  const client = useQueryClient();
  const active = Boolean(user?.uid) && !loading && enabled;
  const signature = JSON.stringify([user?.uid, ...scope]);
  const activeSignature = useRef<string | null>(null);
  activeSignature.current = active ? signature : null;
  const [boundary, setBoundary] = useState<{
    signature: string;
    updates: number;
  } | null>(null);
  const query = useQuery({
    queryKey: ["coaching", user?.uid, ...scope],
    queryFn: ({ signal }) => read(signal),
    enabled: active,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const { refetch } = query;
  const cachedQuery = client
    .getQueryCache()
    .find({ queryKey: ["coaching", user?.uid, ...scope], exact: true });
  useEffect(() => {
    activeSignature.current = active ? signature : null;
    setBoundary(
      active
        ? { signature, updates: cachedQuery?.state.dataUpdateCount ?? 0 }
        : null,
    );
    if (!active) return;
    // Join the mount/re-enable read when already running. Never treat the
    // previous enabled generation's successful response as renewed authority.
    void refetch({ cancelRefetch: false });
    return () => {
      activeSignature.current = null;
      // End the old generation before a disabled view, actor or source can
      // become active again. TanStack discards even late non-abortable replies.
      if (cachedQuery)
        void client.cancelQueries({
          queryKey: cachedQuery.queryKey,
          exact: true,
        });
    };
  }, [active, signature, refetch, cachedQuery, client]);
  // Successful retries advance this counter too; failed/cancelled reads do not.
  const authorized =
    active &&
    boundary?.signature === signature &&
    (cachedQuery?.state.dataUpdateCount ?? 0) > boundary.updates &&
    query.isFetchedAfterMount;
  const isCurrent =
    authorized &&
    query.isSuccess &&
    query.fetchStatus === "idle" &&
    !query.error;
  function canAct() {
    const live = client.getQueryState<T>(["coaching", user?.uid, ...scope]);
    return (
      activeSignature.current === signature &&
      isCurrent &&
      live?.status === "success" &&
      live.fetchStatus === "idle" &&
      !live.isInvalidated &&
      live.data === query.data
    );
  }
  // A warm actor cache is not proof that purpose/role access remains current.
  // Disabled or auth-loading readers must not expose it or spin indefinitely.
  return {
    ...query,
    isCurrent,
    canAct,
    // Already-authorized same-generation state for hidden/inert controllers.
    // This is never permission to display it while the fresh read settles.
    retainedData: authorized ? query.data : undefined,
    isLoading: active && !query.isError && (!authorized || query.isLoading),
    data: authorized && !query.isError ? query.data : undefined,
  };
}

export function useCoachingWorkspace(enabled = true, offset = 0) {
  return useCoachingRead(
    ["workspace", offset],
    (signal) => coachingApi.listWorkspace({ limit: 100, offset }, signal),
    enabled,
  );
}
export function useCoachingProposals(requestId: string, enabled = true) {
  return useCoachingRead(
    ["history", requestId],
    (signal) => coachingApi.listProposals(requestId, signal),
    Boolean(requestId) && enabled,
  );
}
export function useCoachingRequest(requestId: string) {
  return useCoachingRead(
    ["request", requestId],
    (signal) => coachingApi.getWorkspace(requestId, signal),
    Boolean(requestId),
  );
}
export function useCoachingAvailabilityPreview(
  requestId: string, query: CoachingAvailabilityPreviewQuery | null, enabled = true,
) {
  return useCoachingRead(
    ["availability", requestId, query?.start_on, query?.days, query?.duration_minutes, query?.court_id],
    (signal) => coachingApi.previewAvailability(requestId, query!, signal),
    Boolean(requestId && query) && enabled,
  );
}
export function useCoachingAvailabilityCheck(
  requestId: string, body: CoachingAvailabilityCheckInput | null, enabled = true,
) {
  return useCoachingRead(
    ["availability-check", requestId, body ? JSON.stringify(body) : null],
    (signal) => coachingApi.checkAvailability(requestId, body!, signal),
    Boolean(requestId && body?.sessions.length) && enabled,
  );
}
export function useCoachingInquiry(conversationId: string, enabled = true) {
  return useCoachingRead(
    ["inquiry", conversationId],
    (signal) => coachingApi.getInquiryContext(conversationId, signal),
    Boolean(conversationId) && enabled,
  );
}
/** Mount-local authorized offer pages; older history loads only on request. */
export function useCoachingInquiryOffers(conversationId: string, enabled = true) {
  const { user, loading } = useAuth();
  const instance = useId();
  const active = Boolean(user?.uid && !loading && conversationId && enabled);
  const query = useInfiniteQuery({
    queryKey: ["coaching", user?.uid, "inquiry-offers", conversationId, instance],
    enabled: active,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const page = await coachingApi.listInquiryOffers(conversationId,
        { limit: 50, ...(pageParam ? { cursor: pageParam } : {}) }, signal);
      if (page.has_more && (!page.next_cursor || page.next_cursor === pageParam))
        throw new InvalidApiResponseError(`/lesson-requests/inquiries/${conversationId}/offers`);
      return page;
    },
    getNextPageParam: (page) => page.has_more ? page.next_cursor ?? undefined : undefined,
    retry: false,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
  const isCurrent = active && query.isFetchedAfterMount && query.isSuccess &&
    query.fetchStatus === "idle" && !query.error;
  return { ...query, isCurrent, data: isCurrent ? query.data : undefined };
}
export function useCoachingInquiryOffer(conversationId: string, revisionId: string, enabled = true) {
  return useCoachingRead(
    ["inquiry-offer", conversationId, revisionId],
    (signal) => coachingApi.getInquiryOffer(conversationId, revisionId, signal),
    Boolean(conversationId && revisionId && enabled),
  );
}
export function useCoachingGuardianInvitations(offset = 0) {
  return useCoachingRead(["guardian-invitations", offset], (signal) =>
    coachingApi.listGuardianInvitations({ limit: 50, offset }, signal),
  );
}
export function useCoachingProposal(
  requestId: string,
  revisionId: string,
  enabled = true,
) {
  return useCoachingRead(
    ["proposal", requestId, revisionId],
    (signal) => coachingApi.getProposal(requestId, revisionId, signal),
    Boolean(requestId && revisionId) && enabled,
  );
}
export function useCoachingLesson(acceptanceId: string, enabled = true) {
  return useCoachingRead(
    ["lesson", acceptanceId],
    (signal) => coachingApi.getLesson(acceptanceId, signal),
    Boolean(acceptanceId) && enabled,
  );
}
export function useCoachingCancellation(
  acceptanceId: string, commandId: string, enabled = true,
) {
  return useCoachingRead(
    ["cancellation", acceptanceId, commandId],
    (signal) => coachingApi.getCancellation(acceptanceId, commandId, signal),
    Boolean(acceptanceId && commandId) && enabled,
  );
}
export function useCoachingCancellations(acceptanceId: string, enabled = true) {
  return useCoachingRead(
    ["cancellations", acceptanceId],
    (signal) => coachingApi.listCancellations(acceptanceId, signal),
    Boolean(acceptanceId) && enabled,
  );
}
export function useCoachingPayments(enabled = true, offset = 0) {
  return useCoachingRead(
    ["payments", offset],
    (signal) => coachingApi.listPayments({ limit: 100, offset }, signal),
    enabled,
  );
}
export function useCoachingPayment(acceptanceId: string, enabled = true) {
  return useCoachingRead(
    ["payment", acceptanceId],
    (signal) => coachingApi.getPayment(acceptanceId, signal),
    Boolean(acceptanceId) && enabled,
  );
}
export function useCoachingAmendmentContext(
  requestId: string, acceptanceId: string, enabled = true,
) {
  return useCoachingRead(
    ["amendment-context", requestId, acceptanceId],
    (signal) => coachingApi.getAmendmentContext(requestId, acceptanceId, signal),
    Boolean(requestId) && Boolean(acceptanceId) && enabled,
  );
}

function useCoachingCommand<Args extends unknown[], Result>(
  command: (...args: Args) => Promise<Result>,
) {
  const { user } = useAuth();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (args: Args) => command(...args),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["coaching", user?.uid] }),
    onError: (error) => {
      if (
        isApiError(error) &&
        [403, 404, 409].includes(error.status) &&
        error.code !== "PAYMENT_PENDING" &&
        !getPaymentAuthenticationRequired(error)
      ) {
        void client.invalidateQueries({ queryKey: ["coaching", user?.uid] });
      }
    },
  });
}
export const useIssueCoachingProposal = () =>
  useCoachingCommand(coachingApi.issueProposal);
export const useDecideCoachingProposal = () =>
  useCoachingCommand(coachingApi.decideProposal);
export const useAcceptCoachingProposal = () =>
  useCoachingCommand(coachingApi.acceptProposal);
export const useFundCoachingProposal = () =>
  useCoachingCommand(coachingApi.fund);
export const useReconcileCoachingPayment = () =>
  useCoachingCommand(coachingApi.reconcile);
export const useBookCoachingCredit = () =>
  useCoachingCommand(coachingApi.bookCredit);
export const useRecordCoachingSessionOutcome = () =>
  useCoachingCommand(coachingApi.recordSessionOutcome);
export const useCreateCoachingCancellationQuote = () =>
  useCoachingCommand(coachingApi.createCancellationQuote);
export const useConfirmCoachingCancellation = () =>
  useCoachingCommand(coachingApi.confirmCancellation);
export const useReconcileCoachingCancellation = () =>
  useCoachingCommand(coachingApi.reconcileCancellation);
export const useCreateCoachingFromInquiry = () =>
  useCoachingCommand(coachingApi.createFromInquiry);
export const useCreateCoachingGuardianInvitation = () =>
  useCoachingCommand(coachingApi.createGuardianInvitation);
export const useAcceptCoachingGuardianInvitation = () =>
  useCoachingCommand(coachingApi.acceptGuardianInvitation);
export const useRevokeCoachingGuardianInvitation = () =>
  useCoachingCommand(coachingApi.revokeGuardianInvitation);
export type CoachingCommandArguments<K extends keyof CoachingApi> = Parameters<
  CoachingApi[K]
>;
